import {
  batch,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { createStore, produce, reconcile, unwrap } from "solid-js/store";
import { toast } from "solid-sonner";
import { queryClient } from "@/api/client";
import { useI18n } from "@/api/i18n";
import { TALENT_POOL_KEY } from "@/api/query-keys";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { usePostHog } from "@/hooks/usePostHog";
import {
  createMembershipQueryConfig,
  removeTalentPoolFreelancer,
  saveTalentPoolFreelancer,
  updateTalentPoolFreelancer,
  useTalentPoolCapabilities,
} from "../api";
import type {
  ClientMembershipEntry,
  PoolMatchFreelancer,
  Visibility,
} from "../api/types";
import { mapTalentPoolError, type TalentPoolErrorKind } from "../utils/errors";
import { seedMembershipFromMatch } from "../utils/match-membership";
import {
  type MembershipState,
  membershipForWorkspace,
  mergeMembershipPage,
} from "../utils/membership";
import { buildPatchPayload, type PatchDraft } from "../utils/patch-payload";
import { buildSaveContactRequest } from "../utils/save-payload";

/**
 * `TalentPoolProvider` — single source of truth for saved-freelancer state on
 * the active-search result page. Owns the capabilities query, a reactive
 * membership map (`createStore`, workspace-keyed), the upsell modal state, and
 * every save / update / remove / hydrate action. Card hearts and the sheet
 * button read and write exclusively through it, which is what keeps card↔sheet
 * in sync with no search refetch (D9).
 *
 * `save()` promise contract (FROZEN — used by T6b/T8 to open the popover/panel):
 * it RESOLVES with the settled client entry on 201 and on a 409 sync, and it
 * REJECTS only after the provider has already handled the error (optimistic
 * revert + toast / upsell routing). Callers use `save()` solely to decide when
 * to open the popover, never to render errors.
 * Il terzo parametro `searchId` è opzionale e in coda (contratto della promise
 * invariato): serve a inoltrare il `search_id` quando il salvataggio parte
 * dalla lista della ricerca attiva. Chi non lo passa (ogni percorso pool) non
 * invia il campo.
 */

export type SaveSource = "card" | "sheet";

export type TalentPoolCapabilities = {
  readonly enabled: boolean;
  readonly can_share: boolean;
  readonly plan: string | undefined;
  /** The capabilities query settled (success or error) — lets the UI tell
   * "unresolved" (inert) apart from "resolved-as-free" (locked). */
  readonly isResolved: boolean;
  /** The suggested `capabilitiesStatus`: the raw query lifecycle. On failure the
   * UI must stay inert (D16), never assume `free`. */
  readonly status: "pending" | "error" | "success";
};

export type TalentPoolUpsell = {
  readonly isOpen: boolean;
  readonly source: SaveSource | undefined;
  open: (source: SaveSource) => void;
  close: () => void;
};

export type TalentPoolContextValue = {
  capabilities: TalentPoolCapabilities;
  /** Accessor into the reactive membership map. `undefined` ⇒ not saved. */
  entry: (freelancerProfileId: string) => ClientMembershipEntry | undefined;
  /** Non-blocking bulk hydration (deduped, silent on failure). */
  hydrate: (ids: string[]) => void;
  /** Seed membership directly from a match page (OQ1) — no `/membership` call.
   * Dedup-gated on the same `hydratedIds` set as `hydrate`, so it can never
   * resurrect an un-hearted (deleted) entry from cached pages (AC12). */
  seedFromMatch: (items: PoolMatchFreelancer[]) => void;
  /** `searchId` solo dalla ricerca attiva: la lista pool non lo ha. */
  save: (
    freelancerProfileId: string,
    source: SaveSource,
    searchId?: string,
  ) => Promise<ClientMembershipEntry>;
  updateSaved: (
    freelancerProfileId: string,
    patch: { notes?: string | null; visibility?: Visibility },
  ) => Promise<void>;
  remove: (freelancerProfileId: string) => Promise<void>;
  /** Single-id membership refetch (used after a 409); flips `refetchPending`
   * on the entry until it resolves. */
  refetchOne: (freelancerProfileId: string) => void;
  upsell: TalentPoolUpsell;
};

const TalentPoolContext = createContext<TalentPoolContextValue>();

export const useTalentPool = (): TalentPoolContextValue => {
  const ctx = useContext(TalentPoolContext);
  if (!ctx) {
    throw new Error("useTalentPool must be used within a TalentPoolProvider");
  }
  return ctx;
};

export const TalentPoolProvider: ParentComponent = (props) => {
  const { t } = useI18n();
  const posthog = usePostHog();
  const [companyState] = useCompanyStore();
  const [authStore] = useAuthV2();
  const capabilitiesQuery = useTalentPoolCapabilities();

  const companyId = createMemo(() => companyState.selectedCompany?.id);
  // Signed-in profile id — drives `saved_by_me` ownership when seeding pool rows.
  const ownProfileId = () => authStore.profile?.id;

  const [store, setStore] = createStore<MembershipState>({
    companyId: untrack(companyId),
    entries: {},
  });

  // Ids already requested from the membership endpoint — dedup across reactive
  // re-runs and load-more. Cleared on workspace switch.
  const hydratedIds = new Set<string>();

  // Workspace-scoped reset (D9 + FLOW edge case): the pool belongs to the
  // selected company. When the company changes, drop the previous company's
  // entries so saved state never leaks across workspaces. This is a genuine
  // reset side-effect keyed on `companyId`, not derived state.
  createEffect(() => {
    const id = companyId();
    const previous: MembershipState = untrack(() => ({
      companyId: store.companyId,
      entries: store.entries,
    }));
    const next = membershipForWorkspace(previous, id);
    if (next === previous) return;
    batch(() => setStore(reconcile(next)));
    hydratedIds.clear();
  });

  const capabilities: TalentPoolCapabilities = {
    get enabled() {
      return capabilitiesQuery.data?.enabled ?? false;
    },
    get can_share() {
      return capabilitiesQuery.data?.can_share ?? false;
    },
    get plan() {
      return capabilitiesQuery.data?.plan;
    },
    get isResolved() {
      return capabilitiesQuery.isSuccess || capabilitiesQuery.isError;
    },
    get status() {
      if (capabilitiesQuery.isError) return "error";
      if (capabilitiesQuery.isSuccess) return "success";
      return "pending";
    },
  };

  const [isUpsellOpen, setIsUpsellOpen] = createSignal(false);
  const [upsellSource, setUpsellSource] = createSignal<SaveSource | undefined>(
    undefined,
  );

  const upsell: TalentPoolUpsell = {
    get isOpen() {
      return isUpsellOpen();
    },
    get source() {
      return upsellSource();
    },
    open: (source: SaveSource) => {
      batch(() => {
        setUpsellSource(source);
        setIsUpsellOpen(true);
      });
    },
    close: () => {
      batch(() => {
        setIsUpsellOpen(false);
        setUpsellSource(undefined);
      });
    },
  };

  const entry = (
    freelancerProfileId: string,
  ): ClientMembershipEntry | undefined => store.entries[freelancerProfileId];

  // A plain-object snapshot of an entry for optimistic revert (detached from
  // the store proxy so it survives an intermediate delete).
  const snapshotEntry = (
    freelancerProfileId: string,
  ): ClientMembershipEntry | undefined => {
    const existing = untrack(() => store.entries[freelancerProfileId]);
    return existing ? { ...unwrap(existing) } : undefined;
  };

  const removeEntry = (freelancerProfileId: string): void => {
    setStore(
      "entries",
      produce((entries) => {
        delete entries[freelancerProfileId];
      }),
    );
  };

  const hydrate = (ids: string[]): void => {
    const activeCompanyId = untrack(companyId);
    if (!activeCompanyId) return;

    const toFetch = ids.filter((id) => id && !hydratedIds.has(id));
    if (toFetch.length === 0) return;
    for (const id of toFetch) hydratedIds.add(id);

    void (async () => {
      try {
        const page = await queryClient.fetchQuery(
          createMembershipQueryConfig(activeCompanyId, toFetch),
        );
        setStore(
          "entries",
          reconcile(
            mergeMembershipPage(
              untrack(() => unwrap(store.entries)),
              page,
            ),
          ),
        );
      } catch {
        // Silent on failure (D4): hearts stay unhydrated but interactive; the
        // 409-on-save path covers any already-saved freelancer. Allow retry.
        for (const id of toFetch) hydratedIds.delete(id);
      }
    })();
  };

  // Seed the membership map directly from a landed match page (OQ1) — no
  // `/membership` round-trip for pool rows. Dedup-gated on `hydratedIds` (the
  // deletion tombstone, finding #1): once an id is seeded it is marked hydrated,
  // so a later un-heart removal survives a panel remount on cached pages (AC12) —
  // `seedMembershipFromMatch` skips already-hydrated ids and `mergeMembershipPage`
  // preserves only mid-flight entries, never deletions.
  const seedFromMatch = (items: PoolMatchFreelancer[]): void => {
    const activeCompanyId = untrack(companyId);
    if (!activeCompanyId) return;

    const { entries, seededIds } = seedMembershipFromMatch(
      untrack(() => unwrap(store.entries)),
      hydratedIds,
      items,
      untrack(ownProfileId),
    );
    if (seededIds.length === 0) return;

    setStore("entries", reconcile(entries));
    for (const id of seededIds) hydratedIds.add(id);
  };

  const refetchOne = (freelancerProfileId: string): void => {
    const activeCompanyId = untrack(companyId);
    if (!activeCompanyId) return;
    if (!untrack(() => store.entries[freelancerProfileId])) return;

    setStore("entries", freelancerProfileId, "refetchPending", true);

    void (async () => {
      try {
        const page = await queryClient.fetchQuery(
          createMembershipQueryConfig(activeCompanyId, [freelancerProfileId]),
        );
        const fresh = page[freelancerProfileId];
        if (fresh) {
          setStore("entries", freelancerProfileId, {
            ...fresh,
            refetchPending: false,
          });
        } else {
          setStore("entries", freelancerProfileId, "refetchPending", false);
        }
      } catch {
        // Silent: keep the optimistic entry, just clear the pending flag.
        setStore("entries", freelancerProfileId, "refetchPending", false);
      }
    })();
  };

  const saveErrorToast = (kind: TalentPoolErrorKind): string => {
    switch (kind.kind) {
      case "not_available":
        return t("company.talent_pool.toast.not_available");
      case "not_publisher":
        return t("company.talent_pool.toast.not_publisher");
      default:
        return t("company.talent_pool.toast.save_error");
    }
  };

  const save = async (
    freelancerProfileId: string,
    source: SaveSource,
    searchId?: string,
  ): Promise<ClientMembershipEntry> => {
    const plan = capabilities.plan ?? null;

    // Optimistic entry — no `saved_freelancer_id` yet.
    const optimistic: ClientMembershipEntry = {
      visibility: "private",
      saved_by_me: true,
      notes: null,
      refetchPending: false,
    };
    setStore("entries", freelancerProfileId, optimistic);

    posthog.capture("start_talent_pool_save", {
      freelancer_id: freelancerProfileId,
      plan,
      source,
    });

    try {
      const response = await saveTalentPoolFreelancer(
        buildSaveContactRequest(freelancerProfileId, searchId),
      );
      // The fetcher returns the full { success, data } wrapper — unwrap `.data`.
      const saved = response.data;
      const settled: ClientMembershipEntry = {
        saved_freelancer_id: saved.id,
        visibility: saved.visibility,
        saved_by_me: true,
        notes: saved.notes,
        refetchPending: false,
      };
      setStore("entries", freelancerProfileId, settled);
      toast.success(t("company.talent_pool.toast.save_success"));
      posthog.capture("end_talent_pool_save", {
        freelancer_id: freelancerProfileId,
        plan,
        source,
      });
      return settled;
    } catch (error) {
      const kind = mapTalentPoolError(error);

      if (kind.kind === "already_saved") {
        // 409 is caller-scoped: our own record already exists (D5). Keep the
        // entry saved and pull the authoritative notes/visibility via a
        // single-id refetch. Treated as success — no error surfaced.
        refetchOne(freelancerProfileId);
        posthog.capture("end_talent_pool_save", {
          freelancer_id: freelancerProfileId,
          plan,
          source,
        });
        return snapshotEntry(freelancerProfileId) ?? optimistic;
      }

      // Every other error reverts the optimistic entry.
      removeEntry(freelancerProfileId);

      if (kind.kind === "plan_unavailable") {
        // Stale-gating (FLOW error path 6): open the upsell and refresh
        // capabilities so subsequent affordances render locked.
        upsell.open(source);
        void queryClient.invalidateQueries({
          queryKey: [TALENT_POOL_KEY, "capabilities"],
        });
      } else {
        toast.error(saveErrorToast(kind));
      }

      posthog.capture("fail_talent_pool_save", {
        freelancer_id: freelancerProfileId,
        plan,
        source,
        error_type: kind.kind,
      });
      throw error;
    }
  };

  const updateSaved = async (
    freelancerProfileId: string,
    patch: { notes?: string | null; visibility?: Visibility },
  ): Promise<void> => {
    const current = snapshotEntry(freelancerProfileId);
    if (!current) return;
    // D6: a colleague-shared save is read-only — never PATCH it.
    if (current.saved_by_me === false) return;

    const initialDraft: PatchDraft = {
      notes: current.notes,
      visibility: current.visibility,
    };
    const nextDraft: PatchDraft = {
      notes: patch.notes !== undefined ? patch.notes : current.notes,
      visibility:
        patch.visibility !== undefined ? patch.visibility : current.visibility,
    };

    const body = buildPatchPayload(initialDraft, nextDraft);
    // Blind-PATCH guard: nothing the user can edit changed ⇒ no request.
    if (!body) return;

    // Optimistic apply of just the changed fields.
    const optimisticNext: ClientMembershipEntry = {
      ...current,
      notes: body.notes !== undefined ? body.notes : current.notes,
      visibility:
        body.visibility !== undefined ? body.visibility : current.visibility,
    };
    setStore("entries", freelancerProfileId, optimisticNext);

    try {
      const response = await updateTalentPoolFreelancer(
        freelancerProfileId,
        body,
      );
      const saved = response.data;
      setStore("entries", freelancerProfileId, {
        saved_freelancer_id: saved.id,
        visibility: saved.visibility,
        saved_by_me: true,
        notes: saved.notes,
        refetchPending: false,
      });
    } catch {
      // Revert to last persisted values + toast (FLOW error path 5).
      setStore("entries", freelancerProfileId, current);
      toast.error(t("company.talent_pool.toast.update_error"));
    }
  };

  const remove = async (freelancerProfileId: string): Promise<void> => {
    const previous = snapshotEntry(freelancerProfileId);
    // Double-remove is silent — DELETE is idempotent and nothing is saved.
    if (!previous) return;
    // D6: a colleague-shared save is read-only — never DELETE it.
    if (previous.saved_by_me === false) return;

    removeEntry(freelancerProfileId); // optimistic clear

    try {
      await removeTalentPoolFreelancer(freelancerProfileId);
      posthog.capture("remove_talent_pool_freelancer", {
        freelancer_id: freelancerProfileId,
        plan: capabilities.plan ?? null,
      });
    } catch {
      // Revert + toast (FLOW error path 5) — a network/500 must restore state.
      setStore("entries", freelancerProfileId, previous);
      toast.error(t("company.talent_pool.toast.remove_error"));
    }
  };

  const value: TalentPoolContextValue = {
    capabilities,
    entry,
    hydrate,
    seedFromMatch,
    save,
    updateSaved,
    remove,
    refetchOne,
    upsell,
  };

  return (
    <TalentPoolContext.Provider value={value}>
      {props.children}
    </TalentPoolContext.Provider>
  );
};
